import { DateTime } from 'luxon';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  WELLNESS_HISTORY_MONTHS,
  WELLNESS_SCORE_REDUCER_KEY,
  WELLNESS_SCORE_TIME_ZONE,
  type WellnessAggregates,
  type WellnessMetrics,
  type WellnessScore,
  type WellnessScoreRequestError,
  type WellnessScoreStoreState,
  type WellnessScoreSummary,
  type WellnessScoreValue,
  type WellnessTier,
} from './wellnessScoreTypes';

type State = Pick<RootState, typeof WELLNESS_SCORE_REDUCER_KEY>;

export const getStore = (state: State): WellnessScoreStoreState => state[WELLNESS_SCORE_REDUCER_KEY];

export const getWellnessScoreResponse = (state: State): WellnessScore | null => getStore(state).score;
export const getWellnessAggregates = (state: State): WellnessAggregates | null =>
  getWellnessScoreResponse(state)?.aggregates ?? null;
export const getWellnessMetrics = (state: State): WellnessMetrics | null =>
  getWellnessScoreResponse(state)?.metrics ?? null;

export const getWellnessScoreLoading = (state: State): boolean => getStore(state).isLoading;
export const getWellnessScoreError = (state: State): WellnessScoreRequestError | null => getStore(state).error;
export const getWellnessScoreLastUpdatedAt = (state: State): string | null => getStore(state).lastUpdatedAt;

/** True once the first request has settled with a response or an error, so there is something to display. */
export const getWellnessScoreReady = (state: State): boolean =>
  getWellnessScoreResponse(state) !== null || getWellnessScoreError(state) !== null;

/** Whether wellness scores, tiers, and supporting values are displayed from the service instead of the client. */
export const getIsWellnessServerCalcEnabled = (state: RootState): boolean =>
  Boolean(featureFlagsSelectors.getFeatureFlag(state, 'wellnessServerCalcsRelease'));

const toScoreValue = (entry: { score: number | null; tier: WellnessTier | null } | null): WellnessScoreValue => ({
  score: entry?.score ?? null,
  tier: entry?.tier?.level ?? null,
});

/** Scores and tier levels of the last successful response; unavailable entries stay null rather than zero. */
export const getServerWellnessScoreSummary = createAppSelector(
  [getWellnessScoreResponse],
  (score): WellnessScoreSummary | null => {
    if (!score) return null;
    const { aggregates, metrics } = score;
    return {
      total: toScoreValue(aggregates.total),
      groups: {
        control: toScoreValue(aggregates.control),
        growth: toScoreValue(aggregates.growth),
        resilience: toScoreValue(aggregates.resilience),
      },
      metrics: {
        spending: toScoreValue(metrics.spending),
        cashFlow: toScoreValue(metrics.cashFlow),
        creditUtilization: toScoreValue(metrics.creditUtilization),
        netWorth: toScoreValue(metrics.netWorth),
        debt: toScoreValue(metrics.debt),
        creditScore: toScoreValue(metrics.creditScore),
        cushion: toScoreValue(metrics.cushion),
        liquidity: toScoreValue(metrics.liquidity),
        flexibility: toScoreValue(metrics.flexibility),
      },
    };
  },
);

/**
 * First day (ISO date) of each month in the response's history arrays, oldest first. The response has no
 * period labels; QCS uses the six months completed before the request's month in `America/Los_Angeles`, so
 * they are derived from the time the response was received in that zone. Null until a response arrives.
 */
export const getWellnessHistoryMonths = createAppSelector(
  [getWellnessScoreLastUpdatedAt],
  (fetchedAt): readonly string[] | null => {
    if (!fetchedAt) return null;
    const requestMonth = DateTime.fromISO(fetchedAt, { zone: WELLNESS_SCORE_TIME_ZONE }).startOf('month');
    if (!requestMonth.isValid) return null;
    return Array.from({ length: WELLNESS_HISTORY_MONTHS }, (_, index) =>
      requestMonth.minus({ months: WELLNESS_HISTORY_MONTHS - index }).toISODate(),
    ).filter((month): month is string => month !== null);
  },
);
